import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { EditorSection } from "../components/editor/EditorSection";
import { ResultViewer } from "../components/results/ResultViewer";
import { TaskPanel } from "../components/TaskPanel";
import { Toast } from "../components/common/Toast";
import { Loading } from "../components/common/Loading";
import { TraceLogsModal } from "../components/TraceLogsModal";
import { ErrorBanner } from "../components/common/ErrorBanner";
import { SuccessBanner } from "../components/common/SuccessBanner";
import { useResize } from "../hooks/useResize";
import { QUERY_LOADING_STAGE_LABEL, useQueryLoadingStage } from "../hooks/useQueryLoadingStage";
import { useQueryExecution } from "../hooks/useQueryExecution";
import { useTaskLoader } from "../hooks/useTaskLoader";
import { useSqlQuery } from "../hooks/useSqlQuery";
import { useProgressSave } from "../hooks/useProgressSave";
import type { TelegramUser } from "../types";
import { useMobile } from "../hooks/useMobile";
import { AITutorEditorPanel } from "../components/AITutorEditorPanel";
import { SEOHead } from "../components/SEOHead";
import { generateBreadcrumbSchema, SITE_URL } from "../utils/seo";
import {
  AI_TUTOR_ASIDE_WIDTH_KEY,
  AI_TUTOR_MIN_WIDTH,
  AI_TUTOR_MAX_WIDTH,
  AI_TUTOR_DEFAULT_WIDTH,
  AI_TUTOR_DESKTOP_TOP,
} from "../constants/aiTutorLayout";
import { LAYERS } from "../constants/layers";
import { SIMULATOR_MOBILE_COMPACT_CLASS } from "../constants/simulatorMobile";

export const Simulator = ({ user }: { user: TelegramUser | null }) => {
  const navigate = useNavigate();
  const isMobile = useMobile();
  const { task, sql, setSql, loading } = useTaskLoader(user);
  const { saveProgress } = useProgressSave(user);
  const { formatSql, saveQuery, toast } = useSqlQuery(sql, setSql, task);
  const [taskPanelOpen, setTaskPanelOpen] = useState(false);
  const [showAITutor, setShowAITutor] = useState(true);

  /** На мобильной версии при открытии задачи показываем описание сразу (нижний лист). */
  useEffect(() => {
    if (!isMobile || task == null) return;
    setTaskPanelOpen(true);
  }, [task?.id, isMobile]);
  const [lockToast, setLockToast] = useState<string | null>(null);
  const [isTraceModalOpen, setIsTraceModalOpen] = useState(false);
  const [errorBannerVisible, setErrorBannerVisible] = useState(true);
  const [successBannerVisible, setSuccessBannerVisible] = useState(true);
  const handleBugReport = () => {
    window.open("https://t.me/quantilecampbot", "_blank");
  };

  const handleFAQ = () => {
    window.open("/faq", "_blank");
  };


  const handleBack = () => {
    navigate("/simulator");
  };

  // Мемоизируем onProgressSave чтобы избежать пересоздания executeQuery
  const onProgressSave = useMemo(
    () =>
      task
        ? (status: "success" | "error", code?: string, error_msg?: string) => saveProgress(task.id, status, code, error_msg)
        : undefined,
    [task, saveProgress]
  );

  const {
    status,
    errorMsg,
    errorMsgFull,
    result,
    resultColumns,
    executionTimeMs,
    memoryUsageBytes,
    readBytes,
    readRows,
    writtenBytes,
    writtenRows,
    resultBytes,
    resultRows,
    cpuTimeUs,
    traceLogs,
    isLoading,
    runQuery,
    runQueryDebug,
    checkQuery,
    resetResult,
  } = useQueryExecution({
    taskId: task?.id || 0,
    sql,
    onProgressSave,
  });

  const queryLoadingPhase = useQueryLoadingStage(isLoading);

  // Обработчик для Debug кнопки
  const handleDebug = async () => {
    setIsTraceModalOpen(true);
    await runQueryDebug();
  };


  const leftResize = useResize({
    initialSize: 440,
    minSize: 250,
    maxSize: 800,
    direction: "horizontal",
  });

  const editorResize = useResize({
    initialSize: isMobile ? 400 : 320,
    minSize: isMobile ? 200 : 150,
    maxSize: isMobile ? 600 : 800,
    direction: "vertical",
  });

  const [chatPanelMinWidth, setChatPanelMinWidth] = useState(436);
  const aiTutorInitialWidth = useState(() => {
    try {
      const w = localStorage.getItem(AI_TUTOR_ASIDE_WIDTH_KEY);
      if (w) {
        const n = parseInt(w, 10);
        if (n >= AI_TUTOR_MIN_WIDTH && n <= AI_TUTOR_MAX_WIDTH) return n;
      }
    } catch {}
    return AI_TUTOR_DEFAULT_WIDTH;
  })[0];
  const aiTutorResize = useResize({
    initialSize: aiTutorInitialWidth,
    minSize: Math.max(AI_TUTOR_MIN_WIDTH, chatPanelMinWidth),
    maxSize: AI_TUTOR_MAX_WIDTH,
    direction: "horizontal",
    invertHorizontal: true, // правая панель: тянем влево = шире
    onSizeChange: (s) => {
      try {
        localStorage.setItem(AI_TUTOR_ASIDE_WIDTH_KEY, String(s));
      } catch {}
    },
  });

  // Показываем ErrorBanner при появлении новой ошибки
  useEffect(() => {
    if (errorMsg) {
      setErrorBannerVisible(true);
    }
  }, [errorMsg]);

  // Показываем SuccessBanner при успешном выполнении
  useEffect(() => {
    if (status === "success" && !errorMsg) {
      setSuccessBannerVisible(true);
    }
  }, [status, errorMsg]);

  useEffect(() => {
    if (!lockToast) return;
    const t = setTimeout(() => setLockToast(null), 2500);
    return () => clearTimeout(t);
  }, [lockToast]);

  // AI Tutor открыт по умолчанию на desktop (как CoursePage/LessonPage)
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const stored = localStorage.getItem("ai-tutor-simulator-open");
    if (stored !== null) {
      setShowAITutor(mq.matches && stored === "true");
    } else {
      setShowAITutor(mq.matches);
    }
    const handleChange = () => {
      if (!mq.matches) setShowAITutor(false);
    };
    mq.addEventListener("change", handleChange);
    return () => mq.removeEventListener("change", handleChange);
  }, []);
  useEffect(() => {
    localStorage.setItem("ai-tutor-simulator-open", String(showAITutor));
  }, [showAITutor]);

  // В тренажёре бейдж домена фиксируем в фирменном зелёном стиле.
  const domainColor = useMemo(() => {
    if (!task?.domain) return undefined;
    return "bg-primary/10 text-primary border-primary/35 hover:bg-primary/15 hover:border-primary/45";
  }, [task?.domain]);

  if (loading || !task) {
    return <Loading message="Загрузка задачи..." />;
  }

  const taskTitle = task.title || "SQL Задание";
  const taskDescription = task.description 
    ? task.description.replace(/[#*`]/g, '').substring(0, 160) + "..."
    : `Практическое задание по SQL и ClickHouse: ${taskTitle}. Решайте задачи онлайн в интерактивном тренажёре.`;
  const taskUrl = `${SITE_URL}/task/${task.id}`;
  
  // Формируем расширенные keywords на основе задачи
  const taskKeywords = [
    taskTitle,
    "SQL задание",
    "ClickHouse практика",
    "SQL задача",
    task.difficulty ? `SQL ${task.difficulty}` : "",
    task.domain ? `${task.domain} SQL` : "",
    ...(task.tag || []),
    "интерактивный SQL",
    "практика SQL онлайн",
    "ClickHouse запросы",
    "SQL упражнения",
    "реальные SQL задачи"
  ].filter(Boolean).join(", ");

  return (
    <div
      className={`flex flex-1 min-h-0 bg-neutral-light ${
        isMobile
          ? `flex-col overflow-y-auto overflow-x-hidden soft-scrollbar ${SIMULATOR_MOBILE_COMPACT_CLASS}`
          : "flex-row overflow-hidden"
      }`}
      style={{
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: !isMobile && showAITutor
          ? Math.max(aiTutorResize.size, chatPanelMinWidth)
          : "env(safe-area-inset-right)",
      }}
    >
      <SEOHead
        title={`${taskTitle} | SQL Задание | Quantile Camp`}
        description={taskDescription}
        keywords={taskKeywords}
        type="article"
        structuredData={[
          generateBreadcrumbSchema([
            { name: "Главная", url: "/" },
            { name: "Тренажёр", url: "/simulator" },
            { name: taskTitle, url: `/task/${task.id}` }
          ])
        ]}
      />
      {/* Левая панель с описанием задачи */}
      {!isMobile && (
        <div
          ref={leftResize.elementRef}
          data-onboarding-id="task-panel"
          className="flex-shrink-0 flex flex-col bg-white h-full border-r border-neutral-border"
          style={{ 
            width: leftResize.size, 
            minWidth: 250, 
            maxWidth: 800,
          }}
        >
          <TaskPanel
            task={task}
            className="h-full"
            domainColor={domainColor}
            showActionsHeader
            onBack={handleBack}
            onFAQ={handleFAQ}
            onBugReport={handleBugReport}
          />
        </div>
      )}

      {/* Ресайзер левой панели */}
      {!isMobile && (
        <div
          onMouseDown={leftResize.onMouseDown}
          className="relative z-10 flex justify-center cursor-col-resize flex-shrink-0 h-full"
          style={{ 
            width: leftResize.isResizing ? "6px" : "4px", 
            margin: "0 1px",
          }}
        >
          <div className={`rounded transition-colors ${leftResize.isResizing ? "bg-primary/60 w-1.5" : "bg-transparent hover:bg-primary/40 w-1"}`} style={{ height: "100%" }} />
        </div>
      )}

      {/* Центр: редактор и результаты */}
      <div 
        className={`${isMobile ? 'w-full flex-1' : 'flex-1 min-w-0'} flex flex-col ${isMobile ? 'overflow-visible' : 'overflow-hidden'} relative h-full`}
      >
        <div className={`w-full flex flex-col ${isMobile ? '' : 'h-full min-h-0'} relative ${isMobile ? 'px-2 pt-0' : 'px-4 sm:px-5 md:px-6 pt-2'}`}>
          {isMobile && (
            <div className="pt-2 pb-2 flex gap-1.5 sm:gap-2 flex-shrink-0 items-center overflow-x-auto min-w-0">
              <button
                onClick={handleBack}
                className="shrink-0 px-2.5 py-2 text-sm bg-neutral-light border border-neutral-border rounded-lg text-neutral-text hover:bg-primary/10 transition-colors whitespace-nowrap"
              >
                Назад
              </button>
              <button
                data-onboarding-id="mobile-task-panel"
                onClick={() => setTaskPanelOpen(true)}
                className="shrink-0 px-2.5 py-2 text-sm bg-white border border-neutral-border rounded-lg shadow-sm text-neutral-text hover:bg-primary/10 transition-colors whitespace-nowrap"
              >
                Описание задачи
              </button>
              <button
                onClick={() => {
                  setTaskPanelOpen(false);
                  setShowAITutor(true);
                }}
                className={`shrink-0 px-2.5 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                  showAITutor
                    ? "bg-primary/15 text-primary border-2 border-primary/40"
                    : "bg-white border-2 border-neutral-border shadow-sm text-neutral-text hover:bg-primary/10 hover:border-primary/30"
                }`}
                title="AI Tutor"
                aria-label="AI Tutor"
              >
                <MessageCircle size={18} className="shrink-0" />
                <span className={showAITutor ? "text-primary font-medium" : ""}>AI Tutor</span>
              </button>
            </div>
          )}
          {/* Контейнер для Editor и ResultViewer с одинаковой шириной */}
          <div className={`w-full flex flex-col ${isMobile ? '' : ''}`} style={!isMobile ? { height: '100%', alignItems: 'stretch' } : { alignItems: 'stretch' }}>
            <div data-onboarding-id="sql-editor">
              <EditorSection
                sql={sql}
                setSql={setSql}
                editorHeight={editorResize.size}
                editorRef={editorResize.elementRef}
                onMouseDown={editorResize.onMouseDown}
                onTouchStart={editorResize.onTouchStart}
                datasets={task.datasets?.filter((ds): ds is typeof ds & { name: string } => 
                  typeof ds === 'object' && ds !== null && 'name' in ds && typeof ds.name === 'string'
                ) || []}
                domain={task.domain}
                onRun={() => {
                  if (!user) {
                    setLockToast("Авторизуйтесь, чтобы запускать запросы");
                    return;
                  }
                  runQuery(false);
                }}
                onDebug={handleDebug}
                onCheck={checkQuery}
                onFormat={formatSql}
                onReset={resetResult}
                onCopy={saveQuery}
                isCheckLocked={!user}
                onCheckLocked={() => setLockToast("Авторизуйтесь, чтобы воспользоваться проверкой")}
                executionTimeMs={executionTimeMs}
                memoryUsageBytes={memoryUsageBytes}
                readBytes={readBytes}
                readRows={readRows}
                writtenBytes={writtenBytes}
                writtenRows={writtenRows}
                resultBytes={resultBytes}
                resultRows={resultRows}
                cpuTimeUs={cpuTimeUs}
              />
            </div>
            
            {/* Баннер с ошибкой между редактором и результатами */}
            {errorMsg && errorBannerVisible && (
              <ErrorBanner 
                message={errorMsg}
                fullMessage={errorMsgFull || undefined}
                onClose={() => setErrorBannerVisible(false)}
                isCheckError={
                  // Если есть результат, значит запрос выполнился, но проверка не прошла
                  (result.length > 0 || resultColumns.length > 0) && status === "error"
                }
              />
            )}
            
            {/* Баннер успеха между редактором и результатами */}
            {status === "success" && !errorMsg && successBannerVisible && (
              <SuccessBanner 
                onClose={() => setSuccessBannerVisible(false)}
              />
            )}
            
            {/* ResultViewer: mt-0 при видимом баннере (баннер даёт mb-4), иначе mt-4 — единый визуальный зазор 1rem */}
            <div 
              data-onboarding-id="results"
              className={`${isMobile ? 'w-full' : ''} ${isMobile ? "pb-4" : ""} flex flex-col w-full ${isMobile ? '' : ((errorMsg && errorBannerVisible) || (status === "success" && !errorMsg && successBannerVisible) ? 'mt-0' : 'mt-4')} ${isMobile ? '' : 'pb-2'}`}
              style={!isMobile ? { 
                maxHeight: `calc(100vh - var(--header-height, 4.5rem) - ${editorResize.size}px - 1rem - 0.5rem)`,
                minHeight: isLoading ? 'min(200px, 30vh)' : 0,
                overflow: 'hidden',
                height: 'auto',
                flexGrow: 0,
                flexShrink: 0,
                width: '100%',
                maxWidth: '100%'
              } : { 
                width: '100%', 
                maxWidth: '100%', 
                paddingTop: 0, 
                marginTop: 0 
              }}
            >
              {isLoading ? (
                <Loading stage={QUERY_LOADING_STAGE_LABEL[queryLoadingPhase]} />
              ) : (
                <ResultViewer rows={result} columns={resultColumns} error_msg={errorMsg} />
              )}
            </div>
          </div>

          {/* Единый Toast для всех уведомлений - ошибки и успех показываются в баннерах, здесь только системные сообщения */}
          <Toast
            message={
              // Ошибки и успех показываются в баннерах, не показываем их здесь
              lockToast
                ? lockToast
                : toast
                ? toast
                : null
            }
            type={
              lockToast
                ? "info"
                : toast
                ? "info"
                : "info"
            }
            autoHide={!!toast || !!lockToast}
            duration={2500}
          />

          {isMobile && taskPanelOpen && (
            <div className="fixed inset-0 z-50">
              <div className="absolute inset-0 bg-black/30" onClick={() => setTaskPanelOpen(false)} />
              <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-xl max-h-[90%] overflow-hidden flex flex-col">
                <div className="flex items-center justify-between p-3 border-b border-neutral-border flex-shrink-0">
                  <h2 className="text-base font-semibold text-neutral-text">Описание задачи</h2>
                  <button
                    className="text-neutral-text hover:text-primary text-2xl leading-none p-1"
                    onClick={() => setTaskPanelOpen(false)}
                  >
                    ✕
                  </button>
                </div>
                <div className="flex-1 overflow-auto p-3 bg-white soft-scrollbar">
                  <TaskPanel task={task} domainColor={domainColor} compact />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Кнопка развернуть AI Tutor (когда свёрнут) — рядом с панелью */}
      {!showAITutor && (
        <div className="hidden lg:block fixed right-0 top-[calc(var(--header-height,4.5rem)+0.25rem)] z-30 group/expand" data-onboarding-id="ai-tutor-expand">
          <button
            onClick={() => setShowAITutor(true)}
            className="flex h-14 min-w-[4.5rem] px-4 items-center justify-center gap-2 bg-white border-2 border-neutral-border border-r-0 rounded-l-lg shadow-md hover:bg-primary/5 hover:border-primary/40 transition-colors font-medium text-neutral-text hover:text-primary"
            aria-label="Открыть AI Tutor"
            title="Открыть AI Tutor"
          >
            <MessageCircle className="w-6 h-6 text-primary flex-shrink-0" />
            <span className="text-sm whitespace-nowrap">AI Tutor</span>
          </button>
        </div>
      )}

      {/* Ресайзер AI Tutor (fixed, у левого края панели) */}
      {!isMobile && showAITutor && (
        <div
          onMouseDown={aiTutorResize.onMouseDown}
          className="hidden lg:flex fixed z-[92] justify-center cursor-col-resize"
          style={{
            width: "8px",
            top: AI_TUTOR_DESKTOP_TOP,
            bottom: 0,
            right: Math.max(aiTutorResize.size, chatPanelMinWidth) - 4,
          }}
        >
          <div className="w-1 rounded bg-transparent hover:bg-primary/40 transition-colors" style={{ height: "100%" }} />
        </div>
      )}

      {/* Правая колонка: AI Tutor — fixed, full height, перекрывает хедер */}
      {!isMobile && showAITutor && task && (
        <aside
          ref={aiTutorResize.elementRef}
          className="hidden lg:flex flex-col border-l border-neutral-border bg-white overflow-visible"
          style={{
            position: "fixed",
            top: AI_TUTOR_DESKTOP_TOP,
            right: 0,
            bottom: 0,
            width: Math.max(aiTutorResize.size, chatPanelMinWidth),
            zIndex: LAYERS.aiTutorPanel,
          }}
        >
          <AITutorEditorPanel
            user={user}
            taskId={task.id}
            taskTitle={task.title}
            userCode={sql}
            onClose={() => setShowAITutor(false)}
            embedded
            onMinWidthChange={setChatPanelMinWidth}
            queryMetrics={
              executionTimeMs != null || readRows != null || readBytes != null
                ? {
                    execution_time_ms: executionTimeMs ?? undefined,
                    read_rows: readRows ?? undefined,
                    read_bytes: readBytes ?? undefined,
                    memory_usage_bytes: memoryUsageBytes ?? undefined,
                    cpu_time_us: cpuTimeUs ?? undefined,
                  }
                : undefined
            }
          />
        </aside>
      )}

      {/* Mobile: AI Tutor fullscreen overlay */}
      {isMobile && showAITutor && task && (
        <div
          className="lg:hidden fixed inset-x-0 top-[var(--header-height,4.5rem)] bottom-0 flex flex-col bg-white border-t border-neutral-border max-w-full overflow-x-hidden"
          style={{ zIndex: LAYERS.mobileOverlay }}
        >
          <AITutorEditorPanel
            user={user}
            taskId={task.id}
            taskTitle={task.title}
            userCode={sql}
            onClose={() => setShowAITutor(false)}
            embedded
            queryMetrics={
              executionTimeMs != null || readRows != null || readBytes != null
                ? {
                    execution_time_ms: executionTimeMs ?? undefined,
                    read_rows: readRows ?? undefined,
                    read_bytes: readBytes ?? undefined,
                    memory_usage_bytes: memoryUsageBytes ?? undefined,
                    cpu_time_us: cpuTimeUs ?? undefined,
                  }
                : undefined
            }
          />
        </div>
      )}

      {/* Модальное окно с трейс-логами */}
      <TraceLogsModal
        isOpen={isTraceModalOpen}
        onClose={() => setIsTraceModalOpen(false)}
        traceLogs={traceLogs}
        isLoading={isLoading}
        onRerun={runQueryDebug}
      />

    </div>
  );
};
